import React, { useEffect, useState } from 'react';
import { TextField, Button, Box, Typography, Alert, Paper, Link, InputAdornment, IconButton, AlertColor, CircularProgress, Skeleton, Stack } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import { isValidEmail } from '../common/utils';
import ForgotPassword from './forgotPassword';
import axios from 'axios';
import { clearSSOProvider, getMobileAuthParams, getMobileParams, getSSOProvider, MobileAuthParams, SSOProvider, storeMobileParams, storeSSOProvider } from './loginUtils';
import { extractRedirectUrlFromState, generateAuthUrl, oauthProviders } from './oauth2Provider';
import * as uuid from 'uuid';
import SSOMessage from './ssoMessage';

const ADMIN_STATUS = "ADMIN";

export interface AlertData {
  message: string,
  style: AlertColor
}

var params: MobileAuthParams | null = null
var ssoData: any = null

export default function Login() {
  const title = document.querySelector('title')?.textContent.toUpperCase() || 'PROSPÉRENCES';
  const queryParams = new URLSearchParams(window.location.search);
  const redirectUrlAfterLogin = queryParams.get('redirectUrlAfterLogin') || (document.getElementById('login-root') as HTMLElement).getAttribute('data-redirect-url') || '';

  const [pageToDisplay, setPageToDisplay] = useState<'login' | 'forgotPassword' | ''>('')
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [alertData, setAlertData] = useState<AlertData | null>(null);
  const [isMobileSource, setIsMobileSource] = useState(false);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  /****************************************** SSO MOBILE APP ********************************************/
  const redirectToMobile = async (redirectUri: string, ssoProvider?: string, authCode?: string, error?: string, state?: string) => {
    const customSchemeUrl = `${redirectUri}?ssoProvider=${ssoProvider || ''}&auth_code=${authCode || ''}${error ? `&error=${error}` : ''}${state ? `&state=${state}` : ''}`;

    setMessage(`Redirection automatique vers l'application...`);

    setTimeout(() => {
      window.location.href = customSchemeUrl;
    }, 2000);
  };

  const handleMobileSSO = async (params: MobileAuthParams) => {
    try {
      // Call backend to determine SSO
      const response = await axios.get('/getSSOByEmail', { params: { email: params.email } });
      
      const data: string = await response.data.ssoProvider.replace("ADMIN_", "");

      const isSSO = data === "Microsoft" || data === "Google";

      if (isSSO) {
        const provider = oauthProviders[data];
        storeSSOProvider({ name: data, state: params.state, email: params.email });
        window.location.href = generateAuthUrl(provider, params.state, params.email);
      } else {
        // Redirect back to mobile app with error
        redirectToMobile(params.redirect_uri, undefined, undefined, 'sso_failed', params.state);
      }
    } catch (error) {
      console.error('Mobile SSO initiation failed:', error);
      // Redirect back to mobile app with error
      redirectToMobile(params.redirect_uri, undefined, undefined, 'sso_failed', params.state);
    }
  };

  const handleOAuthCallback = async (code: string, state: string, ssoProvider: SSOProvider | null) => {
    if (isProcessingOAuth) {
      return; // Prevent multiple simultaneous processing
    }

    try {
      setIsProcessingOAuth(true);
      setLoading(true);

      // Check for stored mobile params => mobile SSO
      const storedMobileParams = getMobileParams();
      if (storedMobileParams) {
        setMessage('Connexion en cours...');
        setIsMobileSource(true);
        setEmail(storedMobileParams.email);
      } else {
        setEmail(ssoProvider?.email || '');
      }

      const { originalState, redirectUrlAfterLogin } = extractRedirectUrlFromState(state);

      // Use the already retrieved stored params
      const currentMobileParams = storedMobileParams || getMobileParams();

      console.log('OAuth callback redirectUrlAfterLogin:', redirectUrlAfterLogin);

      // Compare state parameter
      if (ssoProvider === null || ssoProvider.state !== originalState) {
        throw new Error('Invalid state parameter');
      }

      if (currentMobileParams) {
        // If mobile SSO, redirect back to app with success code
        sessionStorage.removeItem('mobile_auth_params');
        clearSSOProvider();
        redirectToMobile(currentMobileParams.redirect_uri, ssoProvider?.name, code, undefined, currentMobileParams.state);
      } else {
        // After login redirect => call backend to create session (POST /auth/sso with bearer token)
        try {
          const response = await axios.post(
            `/auth/sso`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${code}`
              },
              params: {
                redirectUrlAfterLogin: redirectUrlAfterLogin,
                ssoProvider: ssoProvider?.name,
              },
              validateStatus: (status) => status < 400,
              withCredentials: true // Important for session cookies,
            }
          )
          if (response.status >= 200 && response.status < 300) {
            clearSSOProvider();
            if (redirectUrlAfterLogin) {
              window.location.href = response.data
            } else {
              window.location.href = '/';
            }
          }
        } catch (error) {
          console.error("Error during post SSO login:", error);
          setLoading(false);
          setAlertData({ message: 'Une erreur est survenue lors de la connexion', style: 'error' });
        }
      }
    } catch (error) {
      console.error('OAuth callback failed:', error);
      clearSSOProvider();
      setLoading(false);
      setAlertData({ message: 'Une erreur est survenue lors de la connexion', style: 'error' });
      if (isMobileSource) {
        const currentMobileParams = getMobileParams();
        if (currentMobileParams) {
          // Redirect back to mobile app with error
          redirectToMobile(currentMobileParams.redirect_uri, ssoProvider?.name, undefined, 'sso_failed', currentMobileParams.state);
        }
      }
    } finally {
      setIsProcessingOAuth(false);
    }
  };

  useEffect(() => {
    // Handle OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    params = getMobileAuthParams();

    if (code && state) {
      // This is an OAuth callback
      // Clear URL parameters immediately to prevent reprocessing
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, document.title, url.toString());

      handleOAuthCallback(code, state, getSSOProvider());
      return;
    }

    // Check if this is a mobile-initiated auth
    if (params) {
      setIsMobileSource(true);
      storeMobileParams(params);
      setMessage('Connexion en cours...');

      // Pre-fill email and start SSO
      handleMobileSSO(params);
    }
    setTimeout(() => {
      setPageToDisplay('login');
    }, 200);
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Paper
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          borderRadius: '25px',
          background: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
        }}
        elevation={0}
      >

        {isMobileSource ?
          <div >
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ mt: 1, fontWeight: 800 }}
              color='primary'
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, textAlign: 'center', wordBreak: 'break-all' }}
              color="primary"
            >
              {message}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <CircularProgress />
            </Box>
            <Typography
              variant="body2"
              sx={{ mt: 2, mb: 2, textAlign: 'center', fontSize: '12px', opacity: 0.7 }}
            >
              Ne fermez pas cette page pendant le processus de redirection.
            </Typography>
          </div>
          : pageToDisplay === 'login' ? (
            <Connection
              title={title}
              setPageToDisplay={setPageToDisplay}
              redirectUrlAfterLogin={redirectUrlAfterLogin}
              loading={loading}
              setLoading={setLoading}
              email={email}
              setEmail={setEmail}
              alertData={alertData}
              setAlertData={setAlertData}
            />
          ) : pageToDisplay === 'forgotPassword' ? (
            <ForgotPassword
              setPageToDisplay={setPageToDisplay}
              email={email}
              setEmail={setEmail}
              alertData={alertData}
              setAlertData={setAlertData}
            />
          )
            : <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Skeleton variant="text" width="60%" height={40} />
              <Skeleton variant="text" height={56} />
              <Skeleton variant="text" height={56} />
            </Box>
        }

        {alertData &&
          <Alert
            severity={alertData.style}
            sx={{ mb: 2, fontSize: 12, alignItems: 'center' }}
          >
            {alertData.message}
          </Alert>
        }

        <Box sx={{ fontSize: 12, display: 'flex', justifyContent: 'center', gap: '10px' }}>
          <Link
            underline="always"
            href="https://www.prosperences.fr/"
            color='inherit'
            target="_blank"
            sx={{ opacity: 0.5 }}
          >
            Site Prospérences
          </Link>
          <Link
            underline="always"
            href="https://www.prosperences.fr/mentions-legales"
            color='inherit'
            target="_blank"
            sx={{ opacity: 0.5 }}
          >
            Mentions légales
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

interface ConnectionProps {
  title: string;
  setPageToDisplay: React.Dispatch<React.SetStateAction<'login' | 'forgotPassword' | ''>>;
  redirectUrlAfterLogin: string;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  alertData: AlertData | null;
  setAlertData: React.Dispatch<React.SetStateAction<AlertData | null>>;
}

function Connection(props: ConnectionProps) {
  const [showPasswordContent, setShowPasswordContent] = React.useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [adminLogin, setAdminLogin] = useState(false);
  const [accountOptions, setAccountOptions] = useState<{ ssoProvider?: string } | null>(null);
  const [selectedAccountType, setSelectedAccountType] = useState<'admin' | 'user' | null>(null);
  const [showSSOMessage, setShowSSOMessage] = useState(false);

  const emailErrorMessage = 'Veuillez entrer une adresse e-mail valide'
  const passwordSubmitErrorMessage = 'Une erreur est survenue lors de la connexion'

  // Reset view when email is updated
  useEffect(() => {
    if (emailValidated || showPassword) {
      setEmailValidated(false);
      setShowPassword(false);
      setAdminLogin(false);
      setAccountOptions(null);
      setSelectedAccountType(null);
    }
  }, [props.email]);

  const handleMicrosoftSSORedirection = () => {
    const provider = oauthProviders["Microsoft"];
    const ssoProvider = "Microsoft";
    const state = uuid.v4();
    storeSSOProvider({ name: ssoProvider, state: state, email: props.email });
    const redirectUrl = generateAuthUrl(provider, state, props.email, props.redirectUrlAfterLogin);
    window.location.href = redirectUrl;
  };

  const handleGoogleSSORedirection = () => {
    const provider = oauthProviders["Google"];
    const ssoProvider = "Google";
    const state = uuid.v4();
    storeSSOProvider({ name: ssoProvider, state: state, email: props.email });
    const redirectUrl = generateAuthUrl(provider, state, props.email, props.redirectUrlAfterLogin);
    window.location.href = redirectUrl;
  };

  const continueSSO = (data: any) => {

    console.log('continueSSO data:', data);

    // If SSO, trigger MSAL login redirect
    const isMicrosoft = (data.ssoProvider === "Microsoft");
    const isGoogle = (data.ssoProvider === "Google");

    if (isMicrosoft) {
      handleMicrosoftSSORedirection();
    } else if (isGoogle) {
      handleGoogleSSORedirection();
    } else if (data.ssoProvider === "ADMIN") { // If admin without account in an organisation 
      setAdminLogin(true);
      setEmailValidated(true);
      setShowPassword(true);
    } else if (data.ssoProvider.startsWith("ADMIN_")) {
      // If both admin and collaborator, let user choose, but show admin login by default
      const ssoPart = data.ssoProvider.split("_")[1];
      console.log('ssoPart:', ssoPart);
      setAccountOptions({
        ssoProvider: ssoPart && ssoPart !== "NOSSO" ? ssoPart : undefined
      });
      setEmailValidated(true);
    } else {
      // Otherwise show password field
      setEmailValidated(true);
      setShowPassword(true);
    }
  }

  // Validate e-mail + check SSO state
  const handleEmailSubmit = async () => {
    props.setAlertData(null);

    if (!isValidEmail(props.email)) {
      props.setAlertData({ message: emailErrorMessage, style: "error" });
      return;
    }

    props.setLoading(true);

    try {
      // Call backend to determine SSO
      const response = await axios.get('/getSSOByEmail', { params: { email: props.email } });
      const data = await response.data;

      ssoData = response.data;

      console.log('response:', response.data);

      console.log('ssoConsentDone:', response.data.ssoConsentDone);
      console.log('ssoProvider:', response.data.ssoProvider);
      console.log('params:', params);

      if(response.data.ssoConsentDone == true) {
        continueSSO(data);
      }
      else if(response.data.ssoProvider) {
        setShowSSOMessage(true);
      } else {
        setEmailValidated(true);
        setShowPassword(true);
      }

      
    } catch (err: any) {
      setEmailValidated(true);
      setShowPassword(true);
    } finally {
      props.setLoading(false);
    }
  };

  // Submit password login
  const handlePasswordLogin = async () => {
    if (!showPassword) {
      // Should not happen as the field is hidden, but just in case
      return;
    }
    props.setAlertData(null);
    props.setLoading(true);

    try {
      console.log('Submitting password login for email:', props.email, ' with adminLogin:', adminLogin);
      const startRoute = adminLogin ? '/authAdmin' : '/auth';
      // Call backend for password login
      const response = await axios.post(
        `${startRoute}${props.redirectUrlAfterLogin ? `?redirectUrlAfterLogin=${encodeURIComponent(props.redirectUrlAfterLogin)}` : ''}`,
        { email: props.email, password },
        {
          validateStatus: (status) => status < 500,
          withCredentials: true // Important for session cookies
        }
      );
      if (response.status >= 200 && response.status < 300) {
        // Handle redirect
        if (adminLogin) {
          window.location.href = '/cn/a/inscription';
        } else if (props.redirectUrlAfterLogin) {
          window.location.href = decodeURIComponent(props.redirectUrlAfterLogin);
        } else {
          console.log('Login successful, but no redirect URL provided. Staying on the same page.', response);
          window.location.reload();
        }
      } else if (response.status === 429) {
        const data = response.data;
        const errorString = data.error || "Trop de tentatives de connexion. Veuillez réessayer plus tard.";
        props.setAlertData({ message: errorString, style: "error" });
        props.setLoading(false);
      } else {
        props.setAlertData({ message: "Une erreur est survenue lors de la connexion", style: "error" });
        props.setLoading(false);
      }
    } catch (err: any) {
      props.setAlertData({ message: passwordSubmitErrorMessage, style: "error" });
      props.setLoading(false);
    }
  };

  const handleAccountSelection = (isAdmin: boolean) => {
    const accountType = isAdmin ? 'admin' : 'user';
    setSelectedAccountType(accountType);
    // Small delay to ensure state updates before showing password field
    if (isAdmin) {
      setAdminLogin(true);
      setShowPassword(true);
    } else {
      const isMicrosoft = (accountOptions?.ssoProvider === "Microsoft");
      const isGoogle = (accountOptions?.ssoProvider === "Google");

      if (isMicrosoft) {
        handleMicrosoftSSORedirection();
      } else if (isGoogle) {
        handleGoogleSSORedirection();
      } else {
        setAdminLogin(false);
        setShowPassword(true);
      }
    }
  };

  const handleClickShowPassword = () => setShowPasswordContent((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleConsentSSOMessage = () => {
    setShowSSOMessage(false);
    continueSSO(ssoData);
  };

  return (
    <>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ mt: 1, fontWeight: 800 }}
        color='primary'
      >
        {props.title}
      </Typography>
      <Typography
        gutterBottom
        sx={{ mt: 1, fontSize: 13, fontWeight: 100, opacity: 0.7 }}
      >
        Bonjour, veuillez-vous connecter afin d'accéder à vos services.
      </Typography>

      {showSSOMessage ? (
        <Box sx={{ mt: 2 }}>
          <SSOMessage
            consentSSOMessageCallback={handleConsentSSOMessage}
          />
        </Box>
      ) : (

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (showPassword) {
            handlePasswordLogin();
          } else {
            handleEmailSubmit();
          }
        }}
        sx={{ mt: 2 }}
      >
        <TextField
          id="email"
          fullWidth
          placeholder="Adresse e-mail *"
          value={props.email}
          onChange={(e) => {
            props.setAlertData(null)
            props.setEmail(e.target.value)
          }}
          disabled={emailValidated}
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setEmailValidated(false)}
                  onMouseDown={handleMouseDownPassword}
                >
                  <EditIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter") handleEmailSubmit();
          }}
          autoFocus
          error={props.alertData !== null && props.alertData.message === emailErrorMessage}
          autoComplete='username email'
        />

        {accountOptions && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Choisissez le type de compte :
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant={selectedAccountType === 'admin' ? "contained" : "outlined"}
                fullWidth
                onClick={() => handleAccountSelection(true)}
                disabled={selectedAccountType !== null}
                sx={{ py: 1.5 }}
              >
                <Stack direction="column" alignItems="center" justifyContent="center" spacing={1}>
                  <Typography>Compte Administrateur</Typography>
                  {/* {selectedAccountType === 'admin' && <Typography variant="caption">✓ Sélectionné</Typography>} */}
                </Stack>
              </Button>
              <Button
                variant={selectedAccountType === 'user' ? "contained" : "outlined"}
                fullWidth
                onClick={() => handleAccountSelection(false)}
                disabled={selectedAccountType !== null}
                sx={{ py: 1.5 }}
              >
                <Stack direction="column" alignItems="center" justifyContent="center" spacing={1}>
                  <Typography>Compte Utilisateur</Typography>
                  {accountOptions.ssoProvider && <Typography variant="caption" display="block">SSO {accountOptions.ssoProvider}</Typography>}
                  {/* {selectedAccountType === 'user' && <Typography variant="caption">✓ Sélectionné</Typography>} */}
                </Stack>
              </Button>
            </Box>
          </Box>
        )}

        {/* PASSWORD FIELD (only if non-SSO) */}
        <TextField
          id="current-password"
          name='password'
          fullWidth
          placeholder="Mot de passe *"
          type={showPasswordContent ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {showPasswordContent ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter") handlePasswordLogin();
          }}
          disabled={props.loading}
          autoFocus
          autoComplete='current-password'
          style={{ display: showPassword ? 'block' : 'none' }}
        />
        <Box sx={{ justifySelf: 'flex-end' }}>
          <Link
            underline="hover"
            sx={{ fontSize: '15px', cursor: 'pointer' }}
            onClick={() => props.setPageToDisplay('forgotPassword')}
          >
            Mot de passe oublié ?
          </Link>
        </Box>
        <Button
          type='submit'
          variant="contained"
          fullWidth
          sx={{
            mt: 3,
            mb: 2,
            py: 1.5,
            fontWeight: 'bold',
            borderRadius: '15px'
          }}
          color='primary'
          disabled={props.loading}
        >
          {props.loading ?
            <CircularProgress size={22} /> :
            showPassword ?
              "Se connecter" :
              "Suivant"
          }
        </Button>
      </Box>
      )}
    </>
  )
}

